import React, {useEffect, useState} from 'react';
import {useDebounce} from 'react-use';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import {StyleSx, Sx} from 'app/types/common';
import {isNil} from 'app/util/isNil';
import {DeviceGroupApiService} from 'app/services/api/deviceGroup/DeviceGroupApiService';
import {CreateGroupDialog} from 'app/components/entities/edge';
import {
  SeatsSummary,
  UsbPairing,
  ValidationMessage,
  useCheckCode,
} from 'app/components/features/pairing';
import {useEdgeGroups} from 'app/components/features/edge';
import {Notifications} from 'app/components/Notifications';
import {CloudApi} from 'app/components/entities/cloud';
import {router} from 'app/router/main';

const PAIR_CODE_TOOLTIP =
  "Your device's pairing code shows on the front screen of device when it's not already paired. For Pearl, it's found on the Epiphan Cloud status page in the Admin panel.";

const formScheme = z.object({
  code: z.string().trim().min(1, {message: 'Required field'}),
  name: z.string().trim().min(1, {message: 'Required field'}),
  group: z.string().optional(),
});
type FormFields = z.infer<typeof formScheme>;

const dividerSx: StyleSx = {'&::before, &::after': {position: 'static'}};
const buttonSx: StyleSx = {mt: 3, mb: 2, alignSelf: 'center'};
const footerSx: StyleSx = {mt: 'auto', mb: 2};

interface Props extends Sx {
  teamId: string;
  paired: number;
  total: number;
  groupAccess: boolean;
  usbPairing: boolean;
  permitModifyPlan: boolean;
  premium: boolean;
}

export function RegularPairing({
  sx,
  teamId,
  paired,
  total,
  permitModifyPlan,
  premium,
  groupAccess,
  usbPairing,
}: Props) {
  const [groupDialog, setGroupDialog] = useState(false);
  const [debouncedCode, setDebouncedCode] = useState('');

  const {
    control,
    formState: {isSubmitting},
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormFields>({
    defaultValues: {code: '', name: '', group: ''},
    mode: 'onChange',
    resolver: zodResolver(formScheme),
  });

  const code = watch('code');

  useDebounce(
    () => {
      if (code) {
        setDebouncedCode(code);
      }
    },
    1000,
    [code],
  );

  useEffect(() => {
    if (!code) {
      setDebouncedCode(code);
    }
  }, [code]);

  const {groups, refetch} = useEdgeGroups({teamId, enabled: groupAccess});

  const [addSeat, setAddSeat] = useState(false);

  const checkState = useCheckCode({teamId, code: debouncedCode, enabled: debouncedCode.length > 0});

  const checking = checkState === 'checking';

  const showSeatsSwitcher = checkState === 'no-seats-expandable' && permitModifyPlan;

  const forceAddSeat = checkState === 'no-seats-expandable' && addSeat;
  const readyToPair = checkState === 'ok' || forceAddSeat;

  const handleCreateGroup = async (name: string) => {
    const newGroup = await DeviceGroupApiService.createGroup(teamId, name);
    await refetch();
    setValue('group', newGroup.id, {shouldValidate: false});
    setGroupDialog(false);
  };

  const handlePair = async (fields: FormFields) => {
    try {
      const res = await CloudApi.pairDevice(teamId, fields.code, {
        name: fields.name,
        groupId: fields.group,
        addSeat: forceAddSeat,
      });
      const deviceId = res.ID;
      router.go('deviceDetails', {deviceId});
    } catch {
      Notifications.addErrorNotification('Failed to pair device');
    }
  };

  return (
    <Stack sx={sx} alignItems="center">
      <Typography variant="h4" fontWeight={600} textAlign="center">
        Pair your device to your <br />
        Epiphan Cloud team account
      </Typography>

      <Stack
        gap={2}
        divider={
          <Divider sx={dividerSx} variant="middle">
            OR
          </Divider>
        }
      >
        <Stack
          mt={4}
          width={380}
          component="form"
          onSubmit={handleSubmit(handlePair, (e) => console.log('submit error', e))}
        >
          <Stack direction="row" alignItems="center" gap={0.5} mb={0.25}>
            <FormLabel sx={{mb: 0}}>Pairing code</FormLabel>

            <Tooltip title={PAIR_CODE_TOOLTIP}>
              <HelpOutlineIcon fontSize="small" color="secondary" />
            </Tooltip>
          </Stack>

          <Controller
            name="code"
            control={control}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                type="text"
                sx={{mb: 0.5}}
                placeholder="Pairing code"
                autoFocus={true}
                fullWidth={true}
                variant="standard"
                error={!isNil(error)}
                disabled={isSubmitting}
                helperText={error?.message ?? ' '}
                inputProps={{'data-id': 'pairing-code-input', readOnly: checking}}
                InputProps={{
                  endAdornment: checking ? <CircularProgress size={24} /> : null,
                }}
                onChange={(e) => {
                  const input = e.target.value;

                  if (input === '' || /^[a-zA-Z0-9]+$/.test(input)) {
                    field.onChange(input);
                  }
                }}
              />
            )}
          />

          <FormLabel sx={{mb: 0.25}}>Device name</FormLabel>

          <Controller
            name="name"
            control={control}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                sx={{mb: 0.5}}
                inputProps={{'data-id': 'device-name-input'}}
                variant="standard"
                fullWidth={true}
                disabled={isSubmitting}
                placeholder="Enter device name"
                error={!isNil(error)}
                helperText={error?.message ?? ' '}
              />
            )}
          />

          {groupAccess && (
            <>
              <Stack
                mb={0.25}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <FormLabel sx={{mb: 0}}>Device group</FormLabel>
                <Button
                  data-id="create-group-btn"
                  sx={{py: 0}}
                  variant="text"
                  color="secondary"
                  disabled={isSubmitting}
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setGroupDialog(true)}
                >
                  Create new group
                </Button>
              </Stack>

              <Controller
                name="group"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    sx={{mb: 3}}
                    data-id="group-input"
                    variant="standard"
                    fullWidth={true}
                    disabled={isSubmitting}
                    displayEmpty={true}
                    MenuProps={{sx: {maxHeight: 300, width: 380}}}
                  >
                    <MenuItem value="">
                      <Typography color="text.disabled">None</Typography>
                    </MenuItem>

                    {groups.map((v) => (
                      <MenuItem data-id={`group-${v.id}`} key={v.id} value={v.id}>
                        <Typography variant="inherit" noWrap={true}>
                          {v.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </>
          )}

          <ValidationMessage sx={{mb: 1}} state={checkState} />

          {showSeatsSwitcher && (
            <FormControlLabel
              sx={{mt: 1, mb: 0, alignSelf: 'center'}}
              control={
                <Switch
                  data-id="add-seat-switch"
                  color="secondary"
                  checked={addSeat}
                  onChange={(e) => setAddSeat(e.target.checked)}
                />
              }
              label="Add one seat into the plan"
            />
          )}

          <LoadingButton
            sx={buttonSx}
            type="submit"
            variant="contained"
            color="secondary"
            loading={isSubmitting}
            disabled={!readyToPair}
          >
            Pair device
          </LoadingButton>
        </Stack>

        {usbPairing && <UsbPairing sx={{mb: 3}} />}
      </Stack>

      {groupAccess && (
        <CreateGroupDialog
          open={groupDialog}
          onCreate={handleCreateGroup}
          onClose={() => setGroupDialog(false)}
        />
      )}

      <SeatsSummary
        sx={footerSx}
        paired={paired}
        premium={premium}
        total={total}
        permitModifyPlan={permitModifyPlan}
      />
    </Stack>
  );
}
